/**
 * Quiz Studio Page (V2 - Multi-Step Wizard)
 *
 * Phase 3 implementation: Guided wizard for quiz configuration.
 * Transforms V1's overwhelming single-page interface into clear 6-step journey.
 */

import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/layout/AppHeader";
import { PageHeader } from "@/components/layout/PageHeader";
import { StepNavigation } from "@/components/studio/StepNavigation";
import { PreviewPanel } from "@/components/studio/PreviewPanel";
import { Step1DatasetSelection } from "@/components/studio/steps/Step1DatasetSelection";
import { Step2PromptConfiguration } from "@/components/studio/steps/Step2PromptConfiguration";
import { Step3FilterConfiguration } from "@/components/studio/steps/Step3FilterConfiguration";
import { Step4ExclusionMap } from "@/components/studio/steps/Step4ExclusionMap";
import { Step4ScopeSettings } from "@/components/studio/steps/Step4ScopeSettings";
import { Step6SaveAndPlay } from "@/components/studio/steps/Step6SaveAndPlay";
import {
  useStudioStore,
  selectCurrentStep,
  selectCompletedSteps,
  selectDataset,
  selectPromptKeys,
  selectIsStepValid,
  goToStep,
  nextStep,
  previousStep,
  resetWizard,
} from "@/stores/useStudioStore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useMemo } from "react";
import { countFilteredFeatures, applyFilters } from "@/utils/filterUtils";
import { countFinalPrompts } from "@/utils/promptGrouping";

export function QuizStudioPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const currentStep = useStudioStore(selectCurrentStep);
  const completedSteps = useStudioStore(selectCompletedSteps);
  const dataset = useStudioStore(selectDataset);
  const promptKeys = useStudioStore(selectPromptKeys);
  const filters = useStudioStore((state) => state.filters);
  const excludedFeatureIds = useStudioStore((state) => state.excludedFeatureIds);
  const isCurrentStepValid = useStudioStore(selectIsStepValid(currentStep));

  // Reset wizard on mount ONLY if there's no loaded dataset
  // This allows "Edit quiz" to work by loading config before navigation
  useEffect(() => {
    // Only reset if there's no dataset loaded (i.e., fresh start)
    if (dataset.collection === null) {
      console.log("📝 Quiz Studio: No dataset loaded, resetting wizard");
      resetWizard();
    } else {
      console.log("📝 Quiz Studio: Dataset already loaded, preserving state for edit");
      console.log("   - Dataset:", dataset.label);
      console.log("   - Features:", dataset.collection?.features.length);
      console.log("   - Prompt keys:", promptKeys);
      console.log("   - Filters:", filters.length);
      console.log("   - Exclusions:", excludedFeatureIds.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps = run only on mount

  // Handle navigation
  const handleStepClick = (step: typeof currentStep) => {
    goToStep(step);
  };

  const handleNext = () => {
    if (isCurrentStepValid) {
      nextStep();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      previousStep();
    }
  };

  const handleExit = () => {
    if (confirm("Exit wizard? Unsaved progress will be lost.")) {
      resetWizard();
      navigate("/");
    }
  };

  // Auto-load dataset from URL parameter
  // Simple rule: If URL has dataset param and we don't have it loaded, dispatch the event
  useEffect(() => {
    const datasetIdFromUrl = searchParams.get("dataset");

    if (datasetIdFromUrl && currentStep === 1) {
      // Check if the URL dataset is different from what's currently loaded
      const isLoadedDataset = dataset.source?.type === "catalogue" && dataset.source.id === datasetIdFromUrl;

      if (!isLoadedDataset) {
        // Dispatch event to Step1 to load this dataset
        window.dispatchEvent(new CustomEvent("autoLoadDataset", {
          detail: { datasetId: datasetIdFromUrl }
        }));
      }
    }
  }, [searchParams, currentStep, dataset.source]);

  // Render current step content
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1DatasetSelection />;
      case 2:
        return <Step2PromptConfiguration />;
      case 3:
        return <Step3FilterConfiguration />;
      case 4:
        return <Step4ExclusionMap />;
      case 5:
        return <Step4ScopeSettings />;
      case 6:
        return <Step6SaveAndPlay />;
      default:
        return null;
    }
  };

  // Calculate stats for preview (memoized to avoid recalculation on every render)
  const { totalFeatureCount, filteredFeatureCount, featureCount, filteredCollection, questionCount } = useMemo(() => {
    const total = dataset.collection?.features.length || 0;
    const filtered = countFilteredFeatures(dataset.collection, filters);
    const final = filtered - excludedFeatureIds.length; // Filtered - excluded

    // Calculate unique prompts (handles grouping by shared values)
    const collection = dataset.collection ? applyFilters(dataset.collection, filters) : null;
    const questions = countFinalPrompts(collection, promptKeys, excludedFeatureIds);

    return {
      totalFeatureCount: total,
      filteredFeatureCount: filtered,
      featureCount: final,
      filteredCollection: collection,
      questionCount: questions,
    };
  }, [dataset.collection, filters, excludedFeatureIds, promptKeys]);

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* Global Navigation */}
      <AppHeader />

      {/* Page Header */}
      <PageHeader
        title="Quiz Studio"
        subtitle="Create Your Custom Quiz"
        actions={
          <Button variant="outline" size="sm" onClick={handleExit}>
            Exit
          </Button>
        }
      />

      {/* Step Navigation */}
      <StepNavigation
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={handleStepClick}
      />

      {/* Main Content - Responsive Layout */}
      <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
        {/* Left: Step Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
            {renderStep()}

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 1}
                className="w-full sm:w-auto"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              <div className="text-sm text-muted-foreground order-first sm:order-none">
                Step {currentStep} of 6
              </div>

              {/* Next/Skip buttons (hidden on step 6) */}
              {currentStep < 6 && (
                <div className="flex gap-2 w-full sm:w-auto">
                  {/* Skip to final step button (steps 2-5) */}
                  {currentStep >= 2 && (
                    <Button
                      variant="outline"
                      onClick={() => goToStep(6)}
                      disabled={promptKeys.length === 0}
                      className="w-full sm:w-auto"
                    >
                      Skip to Save
                    </Button>
                  )}
                  <Button
                    onClick={handleNext}
                    disabled={!isCurrentStepValid}
                    className="w-full sm:w-auto"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Preview Panel - Hidden on mobile, sidebar on desktop */}
        <PreviewPanel
          featureCount={featureCount}
          totalFeatureCount={totalFeatureCount}
          questionCount={questionCount}
          filterCount={filters.length}
          dataset={dataset}
          filters={filters}
          excludedFeatureIds={excludedFeatureIds}
          promptKeys={promptKeys}
          className="hidden lg:block lg:w-80 xl:w-96 border-l p-6"
        />
      </div>
    </div>
  );
}
